import { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@material-ui/core";
import { AdminContainer } from "../../components/layouts";
import api from "../../api";
import { useUser } from "../../hooks/user";

const useStyles = makeStyles((theme) => ({
  summaryCard: {
    textAlign: "center",
    height: "100%"
  },
  summaryValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  },
  successRate: {
    color: "#4caf50"
  },
  dangerValue: {
    color: "#f44336"
  },
  section: {
    marginTop: theme.spacing(3)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  }
}));

const ISSUE_LABELS = {
  missing_job: "Missing BullMQ job",
  orphan_job: "Orphan job (no DB record)",
  expired_not_disabled: "Expired but still enabled"
};

export default function ScheduleHealthPage() {
  const classes = useStyles();
  const { user } = useUser();
  const isSuperAdmin = user?.roles?.includes("super_admin");

  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncingWorkflowId, setSyncingWorkflowId] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getScheduleHealth().then(api.pickData);
      setHealth(res);
    } catch (err) {
      console.error("Failed to fetch schedule health:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.postScheduleSync().then(api.pickData);
      setSyncResult(res);
      fetchHealth();
    } catch (err) {
      console.error("Failed to sync schedules:", err);
      setSyncResult({ error: "Sync failed. Check console for details." });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncWorkflow = async (workflowId) => {
    setSyncingWorkflowId(workflowId);
    try {
      await api.postScheduleSyncWorkflow(workflowId);
      fetchHealth();
    } catch (err) {
      console.error("Failed to sync workflow:", err);
    } finally {
      setSyncingWorkflowId(null);
    }
  };

  if (loading && !health) {
    return (
      <AdminContainer>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </AdminContainer>
    );
  }

  const mismatched = health?.mismatched || [];
  const recent = health?.recentExecutions || {};
  const mismatchCount = mismatched.length;
  const successRate =
    recent.last24h > 0
      ? Math.round((recent.success / recent.last24h) * 100)
      : 0;

  return (
    <AdminContainer>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" color="primary">Schedule Health</Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setSyncDialogOpen(true)}
            disabled={syncing}
          >
            {syncing ? "Syncing..." : "Sync All"}
          </Button>
        )}
      </Box>

      {/* Summary Cards */}
      {health && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography className={classes.summaryValue}>
                  {health.totalEnabled}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Enabled Schedules
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography className={classes.summaryValue}>
                  {health.bullmqJobCount}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  BullMQ Jobs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography
                  className={`${classes.summaryValue} ${
                    mismatchCount > 0 ? classes.dangerValue : ""
                  }`}
                >
                  {mismatchCount}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Mismatched
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography className={classes.summaryValue}>
                  {recent.last24h || 0}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Last 24h Executions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography
                  className={`${classes.summaryValue} ${classes.successRate}`}
                >
                  {successRate}%
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Success Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Mismatch Table */}
      {mismatched.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Mismatches
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workflow ID</TableCell>
                  <TableCell>Node ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Issue</TableCell>
                  {isSuperAdmin && <TableCell>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {mismatched.map((row, idx) => (
                  <TableRow key={`${row.workflowId}-${row.nodeId}-${idx}`}>
                    <TableCell>{row.workflowId}</TableCell>
                    <TableCell>{row.nodeId}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>
                      {ISSUE_LABELS[row.issue] || row.issue}
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSyncWorkflow(row.workflowId)}
                          disabled={syncingWorkflowId === row.workflowId}
                        >
                          {syncingWorkflowId === row.workflowId
                            ? "Syncing..."
                            : "Sync"}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Sync All Confirmation Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => {
          setSyncDialogOpen(false);
          setSyncResult(null);
        }}
      >
        <DialogTitle>
          {syncResult ? "Sync Result" : "Confirm Sync All"}
        </DialogTitle>
        <DialogContent>
          {!syncResult && (
            <DialogContentText>
              This will synchronize all BullMQ scheduled jobs with the database
              records. Are you sure you want to proceed?
            </DialogContentText>
          )}
          {syncResult && syncResult.error && (
            <DialogContentText>{syncResult.error}</DialogContentText>
          )}
          {syncResult && !syncResult.error && (
            <DialogContentText>
              Added: {syncResult.added ?? 0}
              <br />
              Removed: {syncResult.removed ?? 0}
              <br />
              Unchanged: {syncResult.unchanged ?? 0}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {syncResult ? (
            <Button
              onClick={() => {
                setSyncDialogOpen(false);
                setSyncResult(null);
              }}
              color="primary"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setSyncDialogOpen(false)}
                disabled={syncing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSyncAll}
                color="primary"
                variant="contained"
                disabled={syncing}
              >
                {syncing ? "Syncing..." : "Sync All"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </AdminContainer>
  );
}
